/**
 * Feature-local Vue Router records for the GvG planner surface.
 *
 * Phase 7 moves the planner-owned routes from `@/router/index.ts` into
 * this module; Phase 8 lifts the `/:guildId/calendar` record in too — the
 * calendar surface is feature-owned (`RaidCalendarView.vue` reads the
 * feature store + composables) and registering it from the host router
 * would force `apps/frontend/src/app/router.ts` to import the feature
 * component. `installGvgPlanner()` calls `router.addRoute(record)` for
 * each entry below during app boot, so the host router stays UI-only.
 *
 * Route gates preserved verbatim from Phase 6:
 *
 * - `/:guildId/settings` is `beforeEnter`-gated on
 *   `(store.isOwner || store.isOfficer)` and redirect everyone else to
 *   Calendar. The planner store already wires `isOwner`/`isOfficer` from
 *   `useSession()` (see `features/gvg-planner/stores/session.ts`).
 *
 * - `/:guildId/plan/:raidId` runs the raid-selection cascade via the host router's
 *   `beforeEach` global guard (in `features/gvg-planner/routerGuards.ts`).
 *   `beforeEnter` on the route record would skip on param-only updates
 *   (`/:guildId/plan/1` → `/:guildId/plan/2`), so the host-level guard is the safer seam —
 *   it tracks the regression in
 *   `apps/frontend/src/router/__tests__/plannerRaidNavigation.test.ts`.
 */
import type { RouteRecordRaw } from 'vue-router'

import GvgPlannerView from './GvgPlannerView.vue'
import { useGvgPlannerStore } from './stores/gvgPlannerStore'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { FEATURE_ROUTE_NAMES } from './constants/routes'

export const featureRoutes: ReadonlyArray<RouteRecordRaw> = [
  {
    path: '/:guildId/calendar',
    name: APP_ROUTE_NAMES.Calendar,
    component: () => import('./components/calendar/RaidCalendarView.vue'),
    meta: { title: 'Calendar' },
  },
  {
    path: '/:guildId/plan/:raidId',
    name: FEATURE_ROUTE_NAMES.GvgPlanner,
    component: GvgPlannerView,
    meta: { title: 'GvG Planner' },
  },
  {
    path: '/:guildId/settings',
    name: FEATURE_ROUTE_NAMES.Officers,
    component: () => import('./Officers.vue'),
    meta: { title: 'Guild settings' },
    // Officers is gated: only owners + officers may view it. Anyone else is
    // returned to the same guild's Calendar after the global guard has
    // verified the guild route parameter and made it active.
    beforeEnter: (to) => {
      const store = useGvgPlannerStore()
      if (!store.isOwner && !store.isOfficer) {
        const guildId = to.params['guildId']
        // `store.activeDiscordGuildId` is exposed as a plain `string`
        // here (Pinia auto-unwraps the underlying `Ref<string>`). The
        // previous fallback passed the ref object directly into
        // `params.guildId`, which Vue Router then coerced to `"[object Object]"`
        // in some Vue Router versions. The fallback below always passes
        // a real string and falls back to `'0'` (the host's
        // "no active guild" sentinel) when neither the route param nor
        // the store has a usable value.
        const fallbackGuildId =
          typeof guildId === 'string' && guildId !== ''
            ? guildId
            : (store.activeDiscordGuildId || '0')
        return {
          name: APP_ROUTE_NAMES.Calendar,
          params: { guildId: fallbackGuildId },
        }
      }
      return true
    },
  },
  {
    path: '/:guildId/audit/:raidId?',
    name: FEATURE_ROUTE_NAMES.AuditLog,
    component: () => import('./components/audit/AuditLogView.vue'),
    meta: { title: 'Audit log' },
    beforeEnter: (to) => {
      const store = useGvgPlannerStore()
      const guildId = to.params['guildId']
      const fallbackGuildId =
        typeof guildId === 'string' && guildId !== ''
          ? guildId
          : (store.activeDiscordGuildId || '0')
      const raidId = to.params['raidId']
      const raidIdPresent = raidId !== undefined && raidId !== ''
      if (
        (!store.isOwner && !store.isOfficer)
        || (raidIdPresent && (typeof raidId !== 'string' || !/^[1-9]\d*$/u.test(raidId)))
      ) {
        return {
          name: APP_ROUTE_NAMES.Calendar,
          params: { guildId: fallbackGuildId },
        }
      }
      return true
    },
  },
  {
    path: '/onboarding',
    name: FEATURE_ROUTE_NAMES.Onboarding,
    component: () => import('./components/guild-admin/GuildOnboardingPanel.vue'),
    meta: { title: 'Onboarding' },
  },
  {
    path: '/my-builds',
    name: FEATURE_ROUTE_NAMES.MyBuilds,
    component: () => import('./components/my-builds/MyBuildsView.vue'),
    meta: { title: 'My builds' },
  },
]
